import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";
import { LAST_UPDATED } from "@/lib/legal";

const CookiePolicy = () => {
  const { t, lang } = useLanguage();
  useLocalizedRoute();

  useSEO({
    title: { es: t("seo", "cookiesTitle"), en: t("seo", "cookiesTitle") },
    description: { es: t("seo", "cookiesDesc"), en: t("seo", "cookiesDesc") },
    path: lang === "es" ? "/cookies" : "/en/cookies",
    pathEs: "/cookies",
    pathEn: "/en/cookies",
  });

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-6 pb-20">
        <section className="container max-w-3xl">
          {lang === "es" ? (
            <article className="prose prose-lg max-w-none text-foreground">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{LAST_UPDATED.es}</p>
              <h1 className="font-display text-4xl font-bold mb-8">Política de Cookies</h1>

              <h2>1. ¿Qué son las cookies?</h2>
              <p>
                Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas un sitio
                web. Permiten que el sitio recuerde tus acciones y preferencias durante un periodo de tiempo.
              </p>

              <h2>2. Responsable</h2>
              <p>
                El responsable de las cookies de este sitio es <strong>WP Media LLC</strong>, sociedad de Delaware
                (Estados Unidos), que opera la marca Retail Lemon. Contacto:{" "}
                <a href="mailto:hola@retaillemon.com" className="text-foreground">hola@retaillemon.com</a>.
              </p>

              <h2>3. ¿Qué cookies utilizamos?</h2>
              <h3>Cookies técnicas propias (necesarias)</h3>
              <p>
                Imprescindibles para que la web funcione: sesión, seguridad, idioma y para recordar tu elección de
                cookies. No requieren consentimiento y no pueden desactivarse.
              </p>
              <h3>Cookies analíticas — Google Analytics 4</h3>
              <p>Solo se instalan si las aceptas. Las gestiona Google Tag Manager con el Modo de Consentimiento v2.</p>
              <ul>
                <li><strong>_ga:</strong> distingue usuarios de forma estadística. Duración: hasta 2 años.</li>
                <li><strong>_ga_8J11TS37VH:</strong> mantiene el estado de la sesión de Analytics. Duración: hasta 2 años.</li>
                <li><strong>Proveedor:</strong> Google LLC (EE. UU.). Tipo: de terceros.</li>
              </ul>
              <p>
                Su uso implica una transferencia internacional de datos a EE. UU., amparada en el Marco de Privacidad
                de Datos UE-EE. UU. (Data Privacy Framework) y en las cláusulas contractuales tipo de la Comisión
                Europea.
              </p>
              <h3>Cookies de marketing</h3>
              <p>Actualmente no instalamos cookies publicitarias; si se activaran, solo lo harían con tu consentimiento.</p>

              <h2>4. Cómo aceptar, rechazar o retirar el consentimiento</h2>
              <p>
                En tu primera visita verás un aviso con tres opciones de igual peso: «Aceptar todas», «Rechazar
                todas» y «Configurar». Ninguna categoría opcional viene activada por defecto. Guardamos tu elección
                durante 12 meses.
              </p>
              <p>
                Puedes cambiar o retirar tu consentimiento en cualquier momento desde el enlace{" "}
                <strong>«Configurar cookies»</strong> del pie de página. También puedes borrar las cookies desde
                tu navegador.
              </p>

              <h2>5. Actualización de esta política</h2>
              <p>
                Esta Política de Cookies puede ser actualizada periódicamente para reflejar cambios en las cookies que
                utilizamos o por razones operativas, legales o regulatorias. Te recomendamos visitar esta página
                regularmente.
              </p>

              <h2>6. Contacto</h2>
              <p>
                Si tienes alguna pregunta sobre nuestra Política de Cookies, puedes contactarnos en{" "}
                <a href="mailto:hola@retaillemon.com" className="text-foreground">hola@retaillemon.com</a>. Para más
                información sobre el tratamiento internacional de datos, consulta nuestra{" "}
                <a href="/privacidad" className="text-foreground">Política de Privacidad</a>.
              </p>


              
            </article>
          ) : (
            <article className="prose prose-lg max-w-none text-foreground">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{LAST_UPDATED.en}</p>
              <h1 className="font-display text-4xl font-bold mb-8">Cookie Policy</h1>

              <h2>1. What are cookies?</h2>
              <p>
                Cookies are small text files stored on your device when you visit a website. They allow the site to
                remember your actions and preferences over a period of time.
              </p>

              <h2>2. Controller</h2>
              <p>
                The controller for the cookies on this website is <strong>WP Media LLC</strong>, a Delaware
                company (United States) operating the Retail Lemon brand. Contact:{" "}
                <a href="mailto:hola@retaillemon.com" className="text-foreground">hola@retaillemon.com</a>.
              </p>

              <h2>3. What cookies do we use?</h2>
              <h3>First-party technical cookies (necessary)</h3>
              <p>
                Required for the website to work: session, security, language and remembering your cookie choice.
                They do not require consent and cannot be disabled.
              </p>
              <h3>Analytics cookies — Google Analytics 4</h3>
              <p>Only set if you accept them. Managed through Google Tag Manager with Consent Mode v2.</p>
              <ul>
                <li><strong>_ga:</strong> statistically distinguishes users. Duration: up to 2 years.</li>
                <li><strong>_ga_8J11TS37VH:</strong> keeps the Analytics session state. Duration: up to 2 years.</li>
                <li><strong>Provider:</strong> Google LLC (USA). Type: third-party.</li>
              </ul>
              <p>
                This involves an international data transfer to the USA, covered by the EU-U.S. Data Privacy
                Framework and the European Commission's Standard Contractual Clauses.
              </p>
              <h3>Marketing cookies</h3>
              <p>We currently set no advertising cookies; if enabled, they would only be set with your consent.</p>

              <h2>4. How to accept, reject or withdraw consent</h2>
              <p>
                On your first visit you will see a notice with three equally weighted options: "Accept all",
                "Reject all" and "Configure". No optional category is enabled by default. We store your choice for
                12 months.
              </p>
              <p>
                You can change or withdraw your consent at any time from the <strong>"Cookie settings"</strong> link
                in the footer. You can also delete cookies from your browser.
              </p>

              <h2>5. Updates to this policy</h2>
              <p>
                This Cookie Policy may be updated periodically to reflect changes in the cookies we use or for
                operational, legal or regulatory reasons. We recommend visiting this page regularly.
              </p>

              <h2>6. Contact</h2>
              <p>
                If you have any questions about our Cookie Policy, you can contact us at{" "}
                <a href="mailto:hola@retaillemon.com" className="text-foreground">hola@retaillemon.com</a>. For more
                information about international data processing, see our{" "}
                <a href="/en/privacy-policy" className="text-foreground">Privacy Policy</a>.
              </p>


              
            </article>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CookiePolicy;
